import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    IoArrowBack,
    IoCarOutline,
    IoWalkOutline,
    IoLockOpenOutline,
    IoSettingsOutline,
    IoClose,
    IoCheckmarkCircle,
    IoShieldCheckmarkOutline,
    IoTimeOutline,
    IoKeypad,
    IoAdd,
    IoTrashOutline
} from 'react-icons/io5';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebaseConfig';
import {
    collection,
    doc,
    getDocs,
    onSnapshot,
    setDoc,
    addDoc,
    deleteDoc,
    serverTimestamp
} from 'firebase/firestore';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import './AccesosScreen.css';

// Lista de accesos por defecto (Solo Vigilantes, Comité y Administrador por defecto)
const DEFAULT_DOORS = [
    {
        id: 'door-1',
        name: 'Pluma Entrada Principal',
        type: 'vehicular',
        locationTag: 'Caseta Norte - Carril Entrada',
        gpioPin: 17,
        isActive: true,
        allowedRoles: ['vigilante', 'comite', 'admin']
    },
    {
        id: 'door-2',
        name: 'Pluma Salida Vehicular',
        type: 'vehicular',
        locationTag: 'Caseta Norte - Carril Salida',
        gpioPin: 27,
        isActive: true,
        allowedRoles: ['vigilante', 'comite', 'admin']
    },
    {
        id: 'door-3',
        name: 'Portón Residentes (Automático)',
        type: 'porton',
        locationTag: 'Acceso Poniente',
        gpioPin: 22,
        isActive: true,
        allowedRoles: ['vigilante', 'comite', 'admin']
    },
    {
        id: 'door-4',
        name: 'Puerta Peatonal Caseta',
        type: 'peatonal',
        locationTag: 'Reja Peatonal Principal',
        gpioPin: 23,
        isActive: true,
        allowedRoles: ['vigilante', 'comite', 'admin']
    }
];

const AVAILABLE_ROLES = [
    { key: 'propietario', label: 'Propietarios' },
    { key: 'inquilino', label: 'Inquilinos' },
    { key: 'vigilante', label: 'Vigilantes' },
    { key: 'comite', label: 'Comité' },
    { key: 'admin', label: 'Administrador' }
];

function ControlAccesoRemotoScreen() {
    const navigate = useNavigate();
    const { userData, currentUser } = useAuth();
    const [actionMessage, setActionMessage] = useState('');
    const [openingDoorId, setOpeningDoorId] = useState(null);

    const condoId = userData?.condominioId || 'SOMOS_DEFAULT';

    // Mapeo estricto de roles según la base de datos
    const getEffectiveUserRole = () => {
        const rawRole = (userData?.rol || '').toLowerCase().trim();
        const rawTipo = (userData?.tipoResidente || '').toLowerCase().trim();

        if (['admin', 'superuser', 'masteruser'].includes(rawRole)) return 'admin';
        if (rawRole === 'comite') return 'comite';
        if (rawRole === 'vigilante') return 'vigilante';
        
        if (rawTipo === 'inquilino' || rawRole === 'inquilino') {
            return 'inquilino';
        }

        return 'propietario';
    };

    const effectiveRole = getEffectiveUserRole();
    const isAdmin = effectiveRole === 'admin' || effectiveRole === 'comite';

    const doorsStorageKey = `centinela_doors_${condoId}`;

    const getStoredDoors = () => {
        try {
            const saved = localStorage.getItem(doorsStorageKey);
            if (saved) return JSON.parse(saved);
        } catch (e) {}
        return DEFAULT_DOORS;
    };

    const [doorList, setDoorList] = useState(getStoredDoors);

    // Cargar accesos en tiempo real desde Firestore con sincronización automática
    useEffect(() => {
        const targetCondoId = (userData?.condominioId && userData.condominioId !== 'GLOBAL')
            ? userData.condominioId
            : 'SOMOS_DEFAULT';

        const doorsColRef = collection(db, `condominios/${targetCondoId}/puertas_centinela`);
        const unsubscribe = onSnapshot(doorsColRef, async (snapshot) => {
            if (!snapshot.empty) {
                const loaded = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                setDoorList(loaded);
                localStorage.setItem(doorsStorageKey, JSON.stringify(loaded));
            } else {
                // Si la colección en Firestore está vacía y este usuario es Admin/Comite,
                // subir de inmediato las puertas configuradas en este navegador a Firestore para compartirlas con los celulares
                const localDoors = getStoredDoors();
                if (isAdmin && localDoors && localDoors.length > 0) {
                    console.log("Subiendo puertas locales a Firestore para sincronización global...");
                    for (const d of localDoors) {
                        try {
                            await setDoc(doc(db, `condominios/${targetCondoId}/puertas_centinela`, d.id), {
                                ...d,
                                condominioId: targetCondoId,
                                updatedAt: serverTimestamp()
                            }, { merge: true });
                        } catch (e) {
                            console.error("Error subiendo puerta a Firestore:", e);
                        }
                    }
                }
                setDoorList(localDoors);
            }
        }, async (err) => {
            console.warn("Error escuchando puertas_centinela:", err);
            setDoorList(getStoredDoors());
        });

        return () => unsubscribe();
    }, [condoId, userData, isAdmin]);

    // Modal de Configuración y Creación de Puertas
    const [selectedDoorForConfig, setSelectedDoorForConfig] = useState(null);
    const [isCreatingNewDoor, setIsCreatingNewDoor] = useState(false);
    const [doorConfigForm, setDoorConfigForm] = useState({
        name: '',
        locationTag: '',
        type: 'vehicular',
        gpioPin: 17,
        isActive: true,
        allowedRoles: ['propietario', 'inquilino', 'comite', 'vigilante', 'admin']
    });

    // Disparar Apertura Remota
    const handleTriggerDoor = async (door) => {
        if (openingDoorId) return;
        setOpeningDoorId(door.id);
        setActionMessage(`Enviando señal de apertura a ${door.name}...`);

        const rawHouse = userData?.numeroCasa || userData?.casaNumero || userData?.unidadResidente || userData?.depto || 'S/N';
        const formattedHouse = rawHouse.toString().replace(/^(casa|depto)\s*/i, '');

        // 1. Registrar en la Colección Oficial de Accesos (collection "accesos" en la raíz)
        const accessLogData = {
            tipo: 'Apertura Remota',
            tipoVisita: 'Residente / Apertura Remota',
            nombre: userData?.nombre || userData?.email || 'Residente',
            nombreVisita: `Apertura: ${door.name}`,
            residenteId: currentUser?.uid || 'anonimo',
            usuarioId: currentUser?.uid || 'anonimo',
            numeroCasa: formattedHouse,
            puerta: door.name,
            puertaId: door.id,
            deviceType: door.deviceType || (door.name?.toLowerCase().includes('peatonal') ? 'hikvision' : 'gpio'),
            dispararHardware: true,
            condominioId: condoId,
            rol: effectiveRole,
            tipoResidente: userData?.tipoResidente || (effectiveRole === 'inquilino' ? 'Inquilino' : 'Propietario'),
            estatus: 'Completado',
            usosActuales: 1,
            fecha: serverTimestamp(),
            timestamp: serverTimestamp(),
            fechaTexto: new Date().toLocaleDateString('es-MX'),
            horaTexto: new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
        };

        let openedLocally = false;

        // Intentar apertura directa ultrarrápida vía Gateway Local (LAN / localhost)
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 2000);

            const res = await fetch('http://localhost:4005/api/remote-open', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    doorId: door.id,
                    doorName: door.name,
                    requestedBy: userData?.nombre || userData?.email || 'Residente',
                    userId: currentUser?.uid || null,
                    house: formattedHouse
                }),
                signal: controller.signal
            });
            clearTimeout(timeoutId);

            if (res.ok) {
                const data = await res.json();
                if (data.success) {
                    openedLocally = true;
                    console.log("⚡ Apertura ejecutada directamente vía Gateway Local en LAN.");
                }
            }
        } catch (lanErr) {
            console.log("Aviso: Gateway local no disponible directamente, usando relevo en la nube:", lanErr.message);
        }

        // Si no se abrió vía LAN local, enviar la orden mediante Firestore (Nube)
        if (!openedLocally) {
            try {
                await addDoc(collection(db, "accesos"), accessLogData);
            } catch (e) {
                console.warn("Aviso registrando acceso en la nube:", e.message);
            }
        }

        // 2. Feedback visual
        setTimeout(() => {
            setActionMessage(`✓ ${door.name} accionada correctamente.`);
            setTimeout(() => {
                setActionMessage('');
                setOpeningDoorId(null);
            }, 3000);
        }, 1200);
    };

    // Abrir modal para crear nuevo acceso
    const handleOpenCreateDoorModal = () => {
        setIsCreatingNewDoor(true);
        setSelectedDoorForConfig({ id: 'door-' + Date.now() });
        setDoorConfigForm({
            name: '',
            locationTag: '',
            type: 'vehicular',
            deviceType: 'gpio',
            gpioPin: 17,
            hikvisionIp: '192.168.1.76',
            isActive: true,
            allowedRoles: ['propietario', 'inquilino', 'comite', 'vigilante', 'admin']
        });
    };

    // Abrir configuración de una puerta existente
    const handleOpenDoorConfig = (door) => {
        setIsCreatingNewDoor(false);
        setSelectedDoorForConfig(door);
        setDoorConfigForm({
            name: door.name,
            locationTag: door.locationTag || '',
            type: door.type || 'vehicular',
            deviceType: door.deviceType || (door.name?.toLowerCase().includes('peatonal') ? 'hikvision' : 'gpio'),
            gpioPin: door.gpioPin || 17,
            hikvisionIp: door.hikvisionIp || '192.168.1.76',
            isActive: door.isActive !== false,
            allowedRoles: Array.isArray(door.allowedRoles) ? door.allowedRoles : ['propietario', 'inquilino', 'comite', 'vigilante', 'admin']
        });
    };

    const handleToggleDoorRole = (roleKey) => {
        setDoorConfigForm(prev => {
            const exists = prev.allowedRoles.includes(roleKey);
            const updated = exists
                ? prev.allowedRoles.filter(r => r !== roleKey)
                : [...prev.allowedRoles, roleKey];
            return { ...prev, allowedRoles: updated };
        });
    };

    // Guardar cambios o crear puerta
    const handleSaveDoorConfig = async (e) => {
        e.preventDefault();

        const updatedDoor = {
            id: selectedDoorForConfig.id,
            name: doorConfigForm.name,
            locationTag: doorConfigForm.locationTag,
            type: doorConfigForm.type,
            deviceType: doorConfigForm.deviceType || 'gpio',
            gpioPin: Number(doorConfigForm.gpioPin) || 17,
            hikvisionIp: doorConfigForm.hikvisionIp || '192.168.1.76',
            isActive: doorConfigForm.isActive,
            allowedRoles: doorConfigForm.allowedRoles
        };

        let updatedList;
        if (isCreatingNewDoor) {
            updatedList = [...doorList, updatedDoor];
        } else {
            updatedList = doorList.map(d => d.id === updatedDoor.id ? updatedDoor : d);
        }

        setDoorList(updatedList);
        localStorage.setItem(doorsStorageKey, JSON.stringify(updatedList));

        const targetCondoId = (userData?.condominioId && userData.condominioId !== 'GLOBAL')
            ? userData.condominioId
            : 'SOMOS_DEFAULT';

        try {
            await setDoc(doc(db, `condominios/${targetCondoId}/puertas_centinela`, selectedDoorForConfig.id), {
                ...updatedDoor,
                condominioId: targetCondoId,
                updatedAt: serverTimestamp()
            }, { merge: true });
        } catch (error) {
            console.error("Error guardando puerta en Firestore:", error);
        }

        setSelectedDoorForConfig(null);
    };

    // Forzar sincronización de todas las puertas a la nube
    const handleForceSyncToCloud = async () => {
        const targetCondoId = (userData?.condominioId && userData.condominioId !== 'GLOBAL')
            ? userData.condominioId
            : 'SOMOS_DEFAULT';

        setActionMessage('Sincronizando puertas con la base de datos...');
        try {
            for (const d of doorList) {
                await setDoc(doc(db, `condominios/${targetCondoId}/puertas_centinela`, d.id), {
                    ...d,
                    condominioId: targetCondoId,
                    updatedAt: serverTimestamp()
                }, { merge: true });
            }
            setActionMessage('✓ Puertas sincronizadas con éxito en la nube.');
            setTimeout(() => setActionMessage(''), 3000);
        } catch (err) {
            console.error("Error sincronizando:", err);
            setActionMessage('Error al sincronizar con la nube.');
            setTimeout(() => setActionMessage(''), 3000);
        }
    };

    // Eliminar acceso
    const handleDeleteDoor = async (doorId) => {
        if (!window.confirm('¿Seguro que deseas eliminar este acceso?')) return;

        const updatedList = doorList.filter(d => d.id !== doorId);
        setDoorList(updatedList);
        localStorage.setItem(doorsStorageKey, JSON.stringify(updatedList));

        const targetCondoId = (userData?.condominioId && userData.condominioId !== 'GLOBAL')
            ? userData.condominioId
            : 'SOMOS_DEFAULT';

        try {
            await deleteDoc(doc(db, `condominios/${targetCondoId}/puertas_centinela`, doorId));
        } catch (e) {}

        setSelectedDoorForConfig(null);
    };

    return (
        <div className="ks-page">
            <KondoSidebar activeRoute="/accesos" />
            <main className="ks-main">
                <div className="ks-header-sticky-wrapper">
                    <KondoBrandHeader showBack={false} />
                </div>

                <div className="ks-scroll-area">
                    <div className="accesos-container" style={{ padding: '15px 20px', maxWidth: '1000px', margin: '0 auto' }}>
                        
                        {/* Hero Banner Estándar */}
                        <div className="app-header-sticky" style={{ padding: 0, marginBottom: '20px' }}>
                            <div className="kondo-banner-standard" style={{ background: 'linear-gradient(135deg, #7E66FC 0%, #4F46E5 100%)', margin: 0, padding: '14px 20px', minHeight: 'auto' }}>
                                <div className="banner-standard-content" style={{ width: '100%' }}>
                                    <button onClick={() => navigate(-1)} className="banner-back-btn-standard" aria-label="Volver">
                                        <IoArrowBack size={24} />
                                    </button>
                                    <div className="banner-standard-text">
                                        <h2>Control de Acceso Remoto</h2>
                                        <p>Apertura inteligente de plumas y portones</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Mensaje de Estado / Feedback */}
                        {actionMessage && (
                            <div style={{
                                textAlign: 'center',
                                fontSize: '0.9rem',
                                fontWeight: '700',
                                color: actionMessage.startsWith('✓') ? '#059669' : '#4F46E5',
                                marginBottom: '20px',
                                background: actionMessage.startsWith('✓') ? '#ECFDF5' : '#EEF2FF',
                                padding: '14px',
                                borderRadius: '14px',
                                border: `1px solid ${actionMessage.startsWith('✓') ? '#A7F3D0' : '#C7D2FE'}`,
                                boxShadow: '0 4px 6px rgba(0,0,0,0.04)'
                            }}>
                                {actionMessage}
                            </div>
                        )}

                        {/* Tarjeta de Lista de Accesos */}
                        <div style={{
                            background: 'white',
                            borderRadius: '20px',
                            padding: '24px',
                            boxShadow: '0 4px 15px -1px rgba(0,0,0,0.06)',
                            border: '1px solid #E2E8F0'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#0F172A', fontWeight: '800' }}>Accesos Disponibles</h3>
                                    <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#64748B' }}>
                                        Presiona para accionar la apertura automática de la pluma o portón
                                    </p>
                                </div>
                                {isAdmin && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <button
                                            onClick={handleForceSyncToCloud}
                                            title="Subir y sincronizar accesos a la base de datos de los residentes"
                                            style={{
                                                background: '#F1F5F9',
                                                color: '#475569',
                                                border: '1px solid #CBD5E1',
                                                borderRadius: '10px',
                                                padding: '8px 12px',
                                                fontSize: '0.85rem',
                                                fontWeight: '700',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                            }}
                                        >
                                            <IoShieldCheckmarkOutline size={16} />
                                            <span>Sincronizar a la Nube</span>
                                        </button>
                                        <button
                                            onClick={handleOpenCreateDoorModal}
                                            style={{
                                                background: '#7E66FC',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '10px',
                                                padding: '8px 14px',
                                                fontSize: '0.85rem',
                                                fontWeight: '700',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                boxShadow: '0 4px 10px rgba(126, 102, 252, 0.3)'
                                            }}
                                        >
                                            <IoAdd size={18} />
                                            <span>Nuevo Acceso</span>
                                        </button>
                                        <span style={{
                                            background: '#EEF2FF',
                                            color: '#4F46E5',
                                            fontSize: '0.75rem',
                                            fontWeight: '700',
                                            padding: '6px 12px',
                                            borderRadius: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}>
                                            <IoShieldCheckmarkOutline size={14} /> Modo Admin
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Grid de Puertas */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                                gap: '16px'
                            }}>
                                {doorList.map((door) => {
                                    const canOpen = (door.isActive !== false) && (isAdmin || (Array.isArray(door.allowedRoles) && door.allowedRoles.includes(effectiveRole)));
                                    const isThisOpening = openingDoorId === door.id;
                                    const isDisabledByAdmin = door.isActive === false;

                                    return (
                                        <div
                                            key={door.id}
                                            style={{
                                                background: isDisabledByAdmin ? '#F1F5F9' : canOpen ? '#F8FAFC' : '#F1F5F9',
                                                borderRadius: '16px',
                                                border: `1.5px solid ${isDisabledByAdmin ? '#CBD5E1' : canOpen ? '#E2E8F0' : '#CBD5E1'}`,
                                                padding: '18px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'space-between',
                                                gap: '16px',
                                                opacity: isDisabledByAdmin ? 0.55 : canOpen ? 1 : 0.65,
                                                boxShadow: canOpen && !isDisabledByAdmin ? '0 2px 5px rgba(0,0,0,0.03)' : 'none',
                                                transition: 'all 0.2s ease'
                                            }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    <div style={{
                                                        width: '44px',
                                                        height: '44px',
                                                        borderRadius: '12px',
                                                        background: canOpen && !isDisabledByAdmin ? 'rgba(126, 102, 252, 0.12)' : '#E2E8F0',
                                                        color: canOpen && !isDisabledByAdmin ? '#7E66FC' : '#94A3B8',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}>
                                                        {door.type === 'peatonal' ? <IoWalkOutline size={24} /> : <IoCarOutline size={24} />}
                                                    </div>
                                                    <div>
                                                        <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#1E293B', fontWeight: '800' }}>{door.name}</h4>
                                                        <span style={{ fontSize: '0.75rem', color: '#64748B' }}>
                                                            {door.locationTag} {isAdmin && (
                                                                door.deviceType === 'hikvision' || door.name?.toLowerCase().includes('peatonal') 
                                                                    ? <span style={{ color: '#0284c7', fontWeight: 600 }}>• Terminal Hikvision ({door.hikvisionIp || '192.168.1.76'})</span>
                                                                    : `• Relevador GPIO ${door.gpioPin || 17}`
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>

                                                {isAdmin && (
                                                    <button
                                                        onClick={() => handleOpenDoorConfig(door)}
                                                        title="Configurar permisos por rol y conexión"
                                                        style={{
                                                            background: '#F1F5F9',
                                                            border: 'none',
                                                            borderRadius: '8px',
                                                            width: '32px',
                                                            height: '32px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            cursor: 'pointer',
                                                            color: '#64748B',
                                                            transition: 'all 0.2s'
                                                        }}
                                                    >
                                                        <IoSettingsOutline size={18} />
                                                    </button>
                                                )}
                                            </div>

                                            {isDisabledByAdmin ? (
                                                <div style={{
                                                    textAlign: 'center',
                                                    fontSize: '0.8rem',
                                                    fontWeight: '700',
                                                    color: '#94A3B8',
                                                    padding: '10px 0',
                                                    background: '#F8FAFC',
                                                    borderRadius: '10px'
                                                }}>
                                                    Acceso Deshabilitado Temporalmente
                                                </div>
                                            ) : canOpen ? (
                                                <button
                                                    onClick={() => handleTriggerDoor(door)}
                                                    disabled={!!openingDoorId}
                                                    style={{
                                                        background: isThisOpening ? '#475569' : 'linear-gradient(135deg, #7E66FC 0%, #6366F1 100%)',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '12px',
                                                        padding: '12px 18px',
                                                        fontWeight: '700',
                                                        fontSize: '0.9rem',
                                                        cursor: openingDoorId ? 'not-allowed' : 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '8px',
                                                        boxShadow: isThisOpening ? 'none' : '0 4px 12px rgba(126, 102, 252, 0.35)',
                                                        transition: 'all 0.2s ease'
                                                    }}
                                                >
                                                    <IoLockOpenOutline size={18} />
                                                    <span>{isThisOpening ? 'Enviando señal...' : 'Abrir Acceso'}</span>
                                                </button>
                                            ) : (
                                                <div style={{
                                                    textAlign: 'center',
                                                    fontSize: '0.8rem',
                                                    fontWeight: '700',
                                                    color: '#94A3B8',
                                                    padding: '10px 0',
                                                    background: '#F8FAFC',
                                                    borderRadius: '10px'
                                                }}>
                                                    Sin permiso para tu rol
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                <KondoBottomNav activeRoute="/accesos" />
            </main>

            {/* Modal de Configuración / Nuevo Acceso para Admin */}
            {selectedDoorForConfig && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(15, 23, 42, 0.65)',
                    backdropFilter: 'blur(4px)',
                    zIndex: 9999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px'
                }}>
                    <div style={{
                        background: 'white',
                        borderRadius: '24px',
                        maxWidth: '480px',
                        width: '100%',
                        maxHeight: '90vh',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2)'
                    }}>
                        <div style={{
                            background: 'linear-gradient(135deg, #7E66FC 0%, #6366F1 100%)',
                            color: 'white',
                            padding: '20px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <IoSettingsOutline size={24} />
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '700' }}>
                                        {isCreatingNewDoor ? 'Nuevo Acceso Remoto' : 'Configuración de Acceso'}
                                    </h3>
                                    <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.8)' }}>
                                        Control de hardware IoT y permisos
                                    </span>
                                </div>
                            </div>
                            <button
                                onClick={() => setSelectedDoorForConfig(null)}
                                style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: '4px' }}
                            >
                                <IoClose size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleSaveDoorConfig} style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px', overflowY: 'auto' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>
                                    Nombre del Acceso / Puerta
                                </label>
                                <input
                                    type="text"
                                    value={doorConfigForm.name}
                                    onChange={(e) => setDoorConfigForm({ ...doorConfigForm, name: e.target.value })}
                                    placeholder="Ej. Pluma Norte Entrada"
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '10px 14px',
                                        borderRadius: '10px',
                                        border: '1.5px solid #CBD5E1',
                                        fontSize: '0.9rem',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>
                                        Tipo de Acceso
                                    </label>
                                    <select
                                        value={doorConfigForm.type}
                                        onChange={(e) => setDoorConfigForm({ ...doorConfigForm, type: e.target.value })}
                                        style={{
                                            width: '100%',
                                            padding: '10px 14px',
                                            borderRadius: '10px',
                                            border: '1.5px solid #CBD5E1',
                                            fontSize: '0.9rem',
                                            boxSizing: 'border-box',
                                            background: 'white'
                                        }}
                                    >
                                        <option value="vehicular">🚗 Pluma Vehicular</option>
                                        <option value="porton">🚗 Portón Automático</option>
                                        <option value="peatonal">🚶 Puerta Peatonal</option>
                                    </select>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>
                                        Tipo de Conexión
                                    </label>
                                    <select
                                        value={doorConfigForm.deviceType || (doorConfigForm.type === 'peatonal' ? 'hikvision' : 'gpio')}
                                        onChange={(e) => setDoorConfigForm({ ...doorConfigForm, deviceType: e.target.value })}
                                        style={{
                                            width: '100%',
                                            padding: '10px 14px',
                                            borderRadius: '10px',
                                            border: '1.5px solid #CBD5E1',
                                            fontSize: '0.9rem',
                                            boxSizing: 'border-box',
                                            background: 'white'
                                        }}
                                    >
                                        <option value="gpio">🔌 Relevador GPIO (Raspberry)</option>
                                        <option value="hikvision">📹 Terminal Hikvision (ISAPI)</option>
                                    </select>
                                </div>
                            </div>

                            {doorConfigForm.deviceType === 'hikvision' ? (
                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>
                                        IP Terminal Hikvision
                                    </label>
                                    <input
                                        type="text"
                                        value={doorConfigForm.hikvisionIp || '192.168.1.76'}
                                        onChange={(e) => setDoorConfigForm({ ...doorConfigForm, hikvisionIp: e.target.value })}
                                        placeholder="192.168.1.76"
                                        style={{
                                            width: '100%',
                                            padding: '10px 14px',
                                            borderRadius: '10px',
                                            border: '1.5px solid #CBD5E1',
                                            fontSize: '0.9rem',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>
                            ) : (
                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>
                                        Pin GPIO (Raspberry Pi)
                                    </label>
                                    <input
                                        type="number"
                                        value={doorConfigForm.gpioPin}
                                        onChange={(e) => setDoorConfigForm({ ...doorConfigForm, gpioPin: e.target.value })}
                                        placeholder="17"
                                        required
                                        style={{
                                            width: '100%',
                                            padding: '10px 14px',
                                            borderRadius: '10px',
                                            border: '1.5px solid #CBD5E1',
                                            fontSize: '0.9rem',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>
                            )}

                            <div>
                                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>
                                    Ubicación / Descripción
                                </label>
                                <input
                                    type="text"
                                    value={doorConfigForm.locationTag}
                                    onChange={(e) => setDoorConfigForm({ ...doorConfigForm, locationTag: e.target.value })}
                                    placeholder="Ej. Caseta Norte - Carril Entrada"
                                    style={{
                                        width: '100%',
                                        padding: '10px 14px',
                                        borderRadius: '10px',
                                        border: '1.5px solid #CBD5E1',
                                        fontSize: '0.9rem',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>

                            {/* Habilitar / Deshabilitar Acceso */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                background: '#F8FAFC',
                                padding: '12px 16px',
                                borderRadius: '12px',
                                border: '1px solid #E2E8F0'
                            }}>
                                <div>
                                    <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#1E293B', display: 'block' }}>Estado del Acceso</span>
                                    <span style={{ fontSize: '0.75rem', color: '#64748B' }}>
                                        {doorConfigForm.isActive ? 'Habilitado y operativo' : 'Deshabilitado temporalmente'}
                                    </span>
                                </div>
                                <input
                                    type="checkbox"
                                    checked={doorConfigForm.isActive}
                                    onChange={(e) => setDoorConfigForm({ ...doorConfigForm, isActive: e.target.checked })}
                                    style={{ width: '20px', height: '20px', cursor: 'pointer', accentColor: '#7E66FC' }}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '700', color: '#475569', marginBottom: '8px' }}>
                                    Roles con Permiso de Apertura Remota
                                </label>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                    {AVAILABLE_ROLES.map((role) => {
                                        const isSelected = doorConfigForm.allowedRoles.includes(role.key);
                                        return (
                                            <button
                                                key={role.key}
                                                type="button"
                                                onClick={() => handleToggleDoorRole(role.key)}
                                                style={{
                                                    background: isSelected ? '#7E66FC' : '#F1F5F9',
                                                    color: isSelected ? 'white' : '#475569',
                                                    border: `1.5px solid ${isSelected ? '#7E66FC' : '#E2E8F0'}`,
                                                    borderRadius: '20px',
                                                    padding: '6px 14px',
                                                    fontSize: '0.8rem',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                {isSelected && <IoCheckmarkCircle size={14} />}
                                                <span>{role.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                                {!isCreatingNewDoor && (
                                    <button
                                        type="button"
                                        onClick={() => handleDeleteDoor(selectedDoorForConfig.id)}
                                        title="Eliminar este acceso"
                                        style={{
                                            background: '#FEE2E2',
                                            color: '#EF4444',
                                            border: 'none',
                                            borderRadius: '12px',
                                            padding: '12px',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <IoTrashOutline size={20} />
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => setSelectedDoorForConfig(null)}
                                    style={{
                                        flex: 1,
                                        background: '#F1F5F9',
                                        color: '#475569',
                                        border: 'none',
                                        borderRadius: '12px',
                                        padding: '12px',
                                        fontWeight: '600',
                                        fontSize: '0.9rem',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    style={{
                                        flex: 1.5,
                                        background: '#7E66FC',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '12px',
                                        padding: '12px',
                                        fontWeight: '600',
                                        fontSize: '0.9rem',
                                        cursor: 'pointer',
                                        boxShadow: '0 4px 10px rgba(126, 102, 252, 0.3)'
                                    }}
                                >
                                    {isCreatingNewDoor ? 'Crear Acceso' : 'Guardar Cambios'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ControlAccesoRemotoScreen;
